import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api/client'
import type { CardSelectionRequest } from '../utils/deckSelection'

// Deck-level Fill-all (Prompt 2 backend batch endpoints), consumed by the
// Prompt 8 management chrome: preflight the dictionary/AI split, confirm,
// start, then poll status while the background batch runs.

export interface FillAllPreflightResponse {
  deck_id: string
  total_unfilled: number
  dictionary_count: number
  ai_count: number
  /** What is left of today's AI allowance, and the allowance it is left of.
   * Shown alongside the AI count so the reader can see whether the action
   * fits. Reading these never spends any of it. */
  ai_remaining_budget: number
  ai_daily_limit: number
  /** False when the server could not read the counter. `ai_remaining_budget`
   * is then a fail-open fallback, not a fact, and must not be presented as
   * one — the sheet says the allowance is unknown rather than implying there
   * is no limit. */
  ai_budget_known: boolean
  ai_budget_sufficient: boolean
}

export function useFillAllPreflight() {
  return useMutation({
    mutationFn: async (deckId: string) => {
      const { data } = await api.post<FillAllPreflightResponse>(`/api/v1/decks/${deckId}/fill-all/preflight`)
      return data
    },
  })
}

/** Preview the dictionary/AI split for the multiselect bar's `Fill`, scoped
 * to *selection* rather than the whole deck (brief §23 item 11 / §25 item 7).
 * `Select all` now means the deck (item 9), so this can be as large as
 * Fill-all's own preflight — same "before anything runs" disclosure, same
 * response shape, just computed over the selection's resolved membership.
 * Never mutates a card or spends AI budget (see the backend docstring). */
export function useFillSelectionPreflight() {
  return useMutation({
    mutationFn: async ({ deckId, selection }: { deckId: string; selection: CardSelectionRequest }) => {
      const { data } = await api.post<FillAllPreflightResponse>(
        `/api/v1/decks/${deckId}/cards/fill-preflight`, { selection },
      )
      return data
    },
  })
}

export interface FillAllStartResponse {
  fill_batch_id: string
  deck_id: string
  status: string
  status_url: string
  dictionary_total: number
  ai_total: number
}

export function useStartFillAll() {
  return useMutation({
    mutationFn: async (deckId: string) => {
      const { data } = await api.post<FillAllStartResponse>(`/api/v1/decks/${deckId}/fill-all/start`)
      return data
    },
  })
}

export interface FillBatchStatusResponse {
  fill_batch_id: string
  deck_id: string
  status: 'running' | 'done' | 'stopped' | 'failed'
  dictionary_total: number
  dictionary_filled: number
  ai_total: number
  ai_filled: number
  ai_failed: number
  stop_requested: boolean
  created_at: string
  updated_at: string
}

/** Polls once a second while the batch is running; stops polling the instant
 * a terminal status (done/stopped/failed) is observed. */
export function useFillBatchStatus(fillBatchId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['fill-batch', fillBatchId],
    queryFn: async () => {
      const { data } = await api.get<FillBatchStatusResponse>(`/api/v1/fill-batches/${fillBatchId}/`)
      return data
    },
    enabled: enabled && !!fillBatchId,
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 1000 : false),
  })
}

export function useStopFillBatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (fillBatchId: string) => {
      const { data } = await api.post<FillBatchStatusResponse>(`/api/v1/fill-batches/${fillBatchId}/stop/`)
      return data
    },
    onSuccess: (data) => {
      qc.setQueryData(['fill-batch', data.fill_batch_id], data)
    },
  })
}
